import { useState } from 'react';
import { btn, text } from '../ui';
import { formatZoneLocalWindow } from '../schedule.mjs';

// The order within a zone is the rotation start (week 1): #1 takes the first
// block, #2 the next, and so on; anyone past the block count rests week 1 and
// rotates in later. Reordering here changes the shuffle without touching the
// (fair) rotation math.
export default function SlrRosterPanel({ orderedZones, roster, scheduleStart, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(roster || {});

  const start = () => {
    setDraft(roster || {});
    setEditing(true);
  };
  const move = (zone, i, delta) => {
    const list = draft[zone] || [];
    const j = i + delta;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    setDraft({ ...draft, [zone]: next });
  };
  const save = () => {
    onSave(draft);
    setEditing(false);
  };

  const source = editing ? draft : roster || {};

  return (
    <>
      <div className="flex justify-between items-center mb-1">
        <h2 className="text-lg font-semibold">SLR Roster (Subnet Lead Rotation)</h2>
        {onSave &&
          (editing ? (
            <div className="flex gap-2">
              <button onClick={save} className={btn.save}>Save order</button>
              <button onClick={() => setEditing(false)} className={btn.cancel}>Cancel</button>
            </div>
          ) : (
            <button onClick={start} className={btn.primary}>Edit order</button>
          ))}
      </div>
      <p className={`${text.hint} mb-4`}>
        {editing
          ? 'Order sets week 1: #1 takes the first block, the next takes the following block, and so on. Anyone past the number of blocks rests week 1 and rotates in later.'
          : 'Add people to a zone under Settings, Team members. The order here is the rotation start (week 1).'}
      </p>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {orderedZones.map((zone) => {
          const tz = zone.name;
          const people = source[tz] || [];
          return (
            <div key={tz} className="border rounded-lg overflow-hidden border-gray-300">
              <div className="px-4 py-2 font-medium bg-gray-100">
                {tz}{' '}
                <span className="text-xs font-normal text-gray-500">
                  ({formatZoneLocalWindow(zone, scheduleStart) || `${zone.start} UTC`})
                </span>
              </div>
              <div className="p-4">
                <ol className="text-sm space-y-1">
                  {people.map((name, idx) => (
                    <li key={`${name}-${idx}`} className="flex items-center gap-2">
                      <span className="text-gray-400 w-5 text-right">{idx + 1}.</span>
                      <span className="flex-1">{name}</span>
                      {editing && (
                        <span className="flex gap-1">
                          <button onClick={() => move(tz, idx, -1)} disabled={idx === 0} className={btn.link} title="Move up">↑</button>
                          <button onClick={() => move(tz, idx, 1)} disabled={idx === people.length - 1} className={btn.link} title="Move down">↓</button>
                        </span>
                      )}
                    </li>
                  ))}
                  {people.length === 0 && <li className={text.hint}>Nobody assigned</li>}
                </ol>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
